// POB import helpers — header normalization, fuzzy mapping, date parsing.

import { addDays, format, isValid, parse, parseISO } from 'date-fns';
import type {
  POBEntryInput,
  POBEntryStatus,
  SSEContext,
} from '@/types/pob';

export type MOZAICField =
  | 'first_name'
  | 'last_name'
  | 'company'
  | 'position_title'
  | 'hitch_on_date'
  | 'hitch_off_date'
  | 'status'
  | 'is_sse'
  | 'sse_context'
  | 'sse_notes';

export const REQUIRED_FIELDS: MOZAICField[] = [
  'first_name',
  'last_name',
  'company',
  'position_title',
  'hitch_on_date',
];

export const ALL_FIELDS: { key: MOZAICField; label: string; required: boolean }[] = [
  { key: 'first_name', label: 'First Name', required: true },
  { key: 'last_name', label: 'Last Name', required: true },
  { key: 'company', label: 'Company', required: true },
  { key: 'position_title', label: 'Position Title', required: true },
  { key: 'hitch_on_date', label: 'Hitch On Date', required: true },
  { key: 'hitch_off_date', label: 'Hitch Off Date', required: false },
  { key: 'status', label: 'Status', required: false },
  { key: 'is_sse', label: 'SSE (yes/no)', required: false },
  { key: 'sse_context', label: 'SSE Context', required: false },
  { key: 'sse_notes', label: 'SSE Notes', required: false },
];

export function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/[\s\-]+/g, '_');
}

// True when all required MOZAIC fields are exactly present (after normalization).
export function hasRequiredMozaicColumns(cols: string[]): boolean {
  const normset = new Set(cols.map(normalizeHeader));
  return REQUIRED_FIELDS.every((r) => normset.has(r));
}

// Fuzzy auto-detect: given file columns, suggest a column_map.
// Returned shape: { <source_col_original>: <mozaic_field> }.
export function autoDetectMapping(cols: string[]): Record<string, MOZAICField> {
  const out: Record<string, MOZAICField> = {};
  const taken = new Set<MOZAICField>();

  const patterns: [MOZAICField, RegExp[]][] = [
    ['first_name', [/\bfirst\b/, /\bfname\b/, /\bgiven/]],
    ['last_name', [/\blast\b/, /\blname\b/, /\bsurname\b/, /\bfamily/]],
    ['company', [/^company$/, /\bemployer\b/, /\borg(anization)?\b/, /\bvendor\b/]],
    ['position_title', [/\bposition\b/, /\btitle\b/, /\bjob\b/, /^role$/, /\bcraft\b/]],
    ['hitch_on_date', [/on.?date/, /hitch.?in/, /\bstart\b/, /\barriv/, /check.?in/]],
    ['hitch_off_date', [/off.?date/, /hitch.?out/, /\bend\b/, /depart/, /check.?out/]],
    ['status', [/^status$/, /\bstate\b/]],
    ['is_sse', [/^is_?sse$/, /\bsse\b/, /short.?service/]],
    ['sse_context', [/sse.?context/, /sse.?reason/, /sse.?type/]],
    ['sse_notes', [/sse.?notes?/, /sse.?comment/]],
  ];

  for (const col of cols) {
    const n = normalizeHeader(col);
    for (const [field, regs] of patterns) {
      if (taken.has(field)) continue;
      if (regs.some((r) => r.test(n))) {
        out[col] = field;
        taken.add(field);
        break;
      }
    }
  }

  return out;
}

// Parse a variety of user-supplied date formats into YYYY-MM-DD.
// Returns null on failure. Callers may flag the row but should not block.
export function parseFlexibleDate(raw: unknown): string | null {
  if (raw == null || raw === '') return null;

  // Excel serial numbers come through as numbers (or numeric strings).
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const base = new Date(1899, 11, 30);
    const d = addDays(base, raw);
    return isValid(d) ? format(d, 'yyyy-MM-dd') : null;
  }

  const s = String(raw).trim();
  if (!s) return null;

  if (/^\d{5,6}(\.\d+)?$/.test(s)) {
    // numeric string that looks like an Excel serial
    const serial = parseFloat(s);
    const d = addDays(new Date(1899, 11, 30), serial);
    return isValid(d) ? format(d, 'yyyy-MM-dd') : null;
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = parseISO(s);
    return isValid(d) ? format(d, 'yyyy-MM-dd') : null;
  }

  const formats = [
    'MM/dd/yyyy',
    'M/d/yyyy',
    'dd/MM/yyyy',
    'd/M/yyyy',
    'MM-dd-yyyy',
    'dd-MM-yyyy',
    'MMM d, yyyy',
    'MMMM d, yyyy',
    'd MMM yyyy',
    'd MMMM yyyy',
    'yyyy/MM/dd',
  ];
  for (const fmt of formats) {
    const d = parse(s, fmt, new Date());
    if (isValid(d)) return format(d, 'yyyy-MM-dd');
  }
  return null;
}

function parseBool(raw: unknown): boolean {
  const s = String(raw ?? '').trim().toLowerCase();
  return ['true', '1', 'yes', 'y', 't'].includes(s);
}

const STATUS_SET: POBEntryStatus[] = ['on_board', 'planned', 'departed'];
const SSE_CONTEXT_SET: SSEContext[] = [
  'new_to_industry',
  'new_to_role',
  'new_to_company',
  'returned_from_leave',
];

// Apply a column_map to raw rows, returning POBEntryInput rows plus per-row
// errors. Row numbers are 1-based on the data, so we add +2 to report the
// spreadsheet line number (header = row 1, first data row = row 2).
export function applyMappingToRows(
  rawRows: Record<string, unknown>[],
  columnMap: Record<string, MOZAICField>
): { rows: POBEntryInput[]; errors: string[] } {
  const errors: string[] = [];
  const rows: POBEntryInput[] = [];

  // Invert: mozaic_field → source_col
  const field2col: Partial<Record<MOZAICField, string>> = {};
  for (const [col, field] of Object.entries(columnMap)) {
    field2col[field] = col;
  }

  rawRows.forEach((r, i) => {
    const rowNum = i + 2;
    const pick = (f: MOZAICField): unknown => {
      const col = field2col[f];
      return col ? r[col] : undefined;
    };
    const str = (f: MOZAICField) => String(pick(f) ?? '').trim();

    const first = str('first_name');
    const last = str('last_name');
    const company = str('company');
    const position = str('position_title');
    const hitchOnRaw = pick('hitch_on_date');

    if (!first || !last || !company || !position) {
      errors.push(`Row ${rowNum}: missing required name/company/position`);
      return;
    }
    const hitchOn = parseFlexibleDate(hitchOnRaw);
    if (!hitchOn) {
      errors.push(`Row ${rowNum}: invalid or missing hitch_on_date`);
      return;
    }
    const hitchOff = parseFlexibleDate(pick('hitch_off_date'));

    const statusRaw = normalizeHeader(str('status') || 'on_board') as POBEntryStatus;
    const status = STATUS_SET.includes(statusRaw) ? statusRaw : 'on_board';

    const explicitSseMapped = !!field2col.is_sse;
    const explicitSse = explicitSseMapped ? parseBool(pick('is_sse')) : false;
    const sseCtxRaw = normalizeHeader(str('sse_context'));
    const validCtx: SSEContext | null = SSE_CONTEXT_SET.includes(
      sseCtxRaw as SSEContext
    )
      ? (sseCtxRaw as SSEContext)
      : null;
    const sseNotesRaw = str('sse_notes');

    // Why: treat the row as SSE when EITHER the is_sse column says so OR
    // the file carries any SSE signal (context value or notes). Real
    // operator rosters often flag SSE implicitly via a context/notes
    // column with no separate boolean column. We still accept missing
    // context — the UI flags incomplete rows (amber banner + SSE⚠
    // badge + Publish warning). DB CHECK was dropped in migration
    // 20260414000003.
    const isSse =
      explicitSse ||
      (!explicitSseMapped && (validCtx !== null || sseNotesRaw !== ''));

    rows.push({
      first_name: first,
      last_name: last,
      company,
      position_title: position,
      hitch_on_date: hitchOn,
      hitch_off_date: hitchOff,
      status,
      is_sse: isSse,
      sse_context: isSse ? validCtx : null,
      sse_notes: isSse ? sseNotesRaw || null : null,
    });
  });

  return { rows, errors };
}

// When the file already matches MOZAIC template columns exactly, we build
// an identity mapping and run through applyMappingToRows — single code path.
export function identityMappingFromMozaicHeaders(
  cols: string[]
): Record<string, MOZAICField> {
  const out: Record<string, MOZAICField> = {};
  const knownFields = new Set<string>(ALL_FIELDS.map((f) => f.key));
  for (const col of cols) {
    const n = normalizeHeader(col);
    if (knownFields.has(n)) out[col] = n as MOZAICField;
  }
  return out;
}
